(function () {
  'use strict';

  class SizeGuideDrawer extends HTMLElement {
    connectedCallback() {
      this._handleDocClick = this._handleDocClick.bind(this);
      this._handleDrawerClick = this._handleDrawerClick.bind(this);
      document.addEventListener('click', this._handleDocClick);

      // Move the .size-guide-drawer fixed panel to document.body so it is
      // rendered in the root stacking context. This escapes any stacking
      // context created by ancestor elements (e.g. Horizon's #MainContent
      // page-transition transforms), which would otherwise place the panel
      // beneath breadcrumbs or other positioned UI.
      this._drawer = this.querySelector('.size-guide-drawer');
      if (this._drawer) {
        document.body.appendChild(this._drawer);
        this._drawer.addEventListener('click', this._handleDrawerClick);
        this._initGrabScroll();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._handleDocClick);
      if (this._drawer) {
        this._drawer.removeEventListener('click', this._handleDrawerClick);
      }
    }

    _handleDocClick(e) {
      const trigger = e.target.closest('.size-guide-drawer__trigger-open');
      if (!trigger) return;
      const targetPage = trigger.dataset.sizeGuidePage;
      if (!this._drawer) return;
      if (!targetPage || this._drawer.id === targetPage || this._drawer.id.startsWith(targetPage)) {
        e.preventDefault();
        this.open();
      }
    }

    _handleDrawerClick(e) {
      // Explicit close button (X icon)
      if (e.target.closest('.size-guide-drawer__close')) {
        e.preventDefault();
        this.close();
        return;
      }

      // Tab switcher titles clicked
      const tabTitle = e.target.closest('.size-guide-drawer__tab-title');
      if (tabTitle) {
        e.preventDefault();
        this._switchTab(tabTitle);
        return;
      }

      // Dark overlay area: overlay has pointer-events:none, so clicks on it
      // fall through to the .size-guide-drawer fixed container. Any click
      // that did NOT land inside .size-guide-drawer__wrapper is a dark-area
      // click and should close the drawer.
      if (!e.target.closest('.size-guide-drawer__wrapper')) {
        this.close();
      }
    }

    _switchTab(tabTitle) {
      if (!this._drawer) return;

      const tabTitlesContainer = tabTitle.closest('.size-guide-drawer__tab-titles');
      if (!tabTitlesContainer) return;

      const tabTitles = Array.from(tabTitlesContainer.querySelectorAll('.size-guide-drawer__tab-title'));
      const activeIndex = tabTitles.indexOf(tabTitle);
      if (activeIndex === -1) return;

      // 1. Update tab title classes
      tabTitles.forEach((title, idx) => {
        if (idx === activeIndex) {
          title.classList.add('active');
        } else {
          title.classList.remove('active');
        }
      });

      // 2. Find and update tab content classes
      const tabContentsContainer = this._drawer.querySelector('.size-guide-drawer__tab-contents');
      if (tabContentsContainer) {
        const tabContents = Array.from(tabContentsContainer.querySelectorAll('.size-guide-drawer__tab-content'));
        
        // Try to match by ID if href points to an ID
        let matchedByHref = false;
        const href = tabTitle.getAttribute('href');
        if (href && href.startsWith('#') && href.length > 1) {
          const targetId = href.substring(1);
          const targetContent = tabContentsContainer.querySelector(`#${targetId}`);
          if (targetContent) {
            tabContents.forEach(content => {
              if (content === targetContent) {
                content.classList.add('active');
              } else {
                content.classList.remove('active');
              }
            });
            matchedByHref = true;
          }
        }

        // If not matched by ID, match by index
        if (!matchedByHref && tabContents[activeIndex]) {
          tabContents.forEach((content, idx) => {
            if (idx === activeIndex) {
              content.classList.add('active');
            } else {
              content.classList.remove('active');
            }
          });
        }
      }
    }

    open() {
      if (this._drawer) this._drawer.classList.add('active');
      document.documentElement.classList.add('size-guide-drawer-is-open');
      document.body.classList.add('size-guide-drawer-is-open');
      this._initSwitcher();
    }

    _initSwitcher() {
      if (!this._drawer) return;
      this._drawer.querySelectorAll('.size-guide-switcher input[type="checkbox"]').forEach(function (checkbox) {
        if (checkbox.dataset.switcherBound) return;
        checkbox.dataset.switcherBound = 'true';
        checkbox.addEventListener('change', function () {
          document.body.classList.toggle('switcher-in', this.checked);
        });
      });
    }

    close() {
      if (this._drawer) this._drawer.classList.remove('active');
      document.documentElement.classList.remove('size-guide-drawer-is-open');
      document.body.classList.remove('size-guide-drawer-is-open');
      document.body.classList.remove('switcher-in');
      if (this._drawer) {
        this._drawer.querySelectorAll('.size-guide-switcher input[type="checkbox"]').forEach(function (cb) {
          cb.checked = false;
        });
      }
    }

    _initGrabScroll() {
      if (!this._drawer) return;
      this._drawer.querySelectorAll('.table-responsive').forEach(function (el) {
        var pos = {};
        el.style.cursor = 'grab';

        var onMove = function (e) {
          el.scrollLeft = pos.left - (e.clientX - pos.x);
        };

        var onUp = function () {
          el.style.cursor = 'grab';
          el.style.removeProperty('user-select');
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
        };

        el.addEventListener('mousedown', function (e) {
          pos = { left: el.scrollLeft, x: e.clientX };
          el.style.cursor = 'grabbing';
          el.style.userSelect = 'none';
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        });
      });
    }
  }

  if (!customElements.get('size-guide-drawer')) {
    customElements.define('size-guide-drawer', SizeGuideDrawer);
  }
})();
